<script setup lang="ts">
import type { HTMLAttributes } from 'vue';
import { CircleHelp, Facebook, Instagram, Mail, Phone } from 'lucide';
import { computed } from 'vue';
import { SphIcon } from '@/components/base/icon';
import { SphLink } from '@/components/base/link';
import { useI18n } from '@/composables/i18n';
import { cn } from '@/utils/shadcn/lib/utils';

defineOptions({
    compatConfig: {
        MODE: 3,
    },
});

const props = withDefaults(defineProps<{
    /** As printed, e.g. `+49 (0)40 3030 66 999`; the `tel:` link is derived from it. */
    phone?: string
    email?: string
    helpUrl?: string
    facebookUrl?: string
    instagramUrl?: string
    class?: HTMLAttributes['class']
}>(), {
    phone: undefined,
    email: undefined,
    helpUrl: undefined,
    facebookUrl: undefined,
    instagramUrl: undefined,
    class: '',
});

const { i18n } = useI18n();

const hours = computed(() => i18n.t('Mo – Fr, 08.00 – 17.00 Uhr'));

// The trunk prefix in `(0)` is only dialled from within the country, never after `+49`.
const TRUNK_PREFIX = /\(0\)/g;
const NOT_DIALLED = /[^\d+]/g;

const phoneHref = computed(() => props.phone && `tel:${props.phone.replace(TRUNK_PREFIX, '').replace(NOT_DIALLED, '')}`);

const contacts = computed(() => [
    props.phone && { icon: Phone, href: phoneHref.value, label: props.phone },
    props.email && { icon: Mail, href: `mailto:${props.email}`, label: props.email },
    props.helpUrl && { icon: CircleHelp, href: props.helpUrl, label: i18n.t('Hilfe & FAQ') },
].filter(Boolean) as { icon: typeof Phone, href: string, label?: string }[]);

// Brand names, the same in every language.
const socials = computed(() => [
    props.facebookUrl && { icon: Facebook, href: props.facebookUrl, label: 'Facebook' },
    props.instagramUrl && { icon: Instagram, href: props.instagramUrl, label: 'Instagram' },
].filter(Boolean) as { icon: typeof Facebook, href: string, label: string }[]);
</script>

<!--
    The page's support bar under the content column, in the brand's accent like the header above.
    Narrow, the support line and the contacts stack and centre.

    `relative` contains the links' absolutely positioned `sr-only` hints: the layout's scrolling
    `__main` is not positioned, so they would sit at their static place far down the scrolled content,
    measured from the document instead, and stretch the document by the page's whole height.
-->
<template>
    <footer
        data-slot="page-footer"
        :class="cn('@container/footer bg-primary text-primary-foreground relative print:hidden', props.class)"
    >
        <div class="sph-page-footer__bar flex flex-col items-center gap-x-8 gap-y-3 px-5 py-4 text-sm @4xl/footer:flex-row @4xl/footer:justify-between">
            <!-- Below `xl` the hours take a line of their own under the label; from there on they sit beside it. -->
            <p class="shrink flex flex-wrap items-baseline justify-center gap-x-3 text-center @4xl/footer:justify-start @4xl/footer:text-left">
                <span class="font-semibold">{{ i18n.t('Telefonischer Kundensupport') }}</span>
                <!-- Empty where a configuration names no hours, e.g. one that refers to its own office. -->
                <span
                    v-if="hours"
                    class="flex-auto shrink text-xs opacity-80 max-xl:basis-full whitespace-nowrap"
                >{{ hours }}</span>
            </p>

            <div class="basis-auto grow shrink flex flex-nowrap items-center justify-center gap-x-6 gap-y-3">
                <ul class="basis-auto grow no-shrink flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
                    <li
                        v-for="contact in contacts"
                        :key="contact.href"
                        class="inline-flex items-center gap-2"
                    >
                        <SphIcon
                            :icon="contact.icon"
                            size="sm"
                        />
                        <!--
                            Underlined on hover only; `!` because the link's own unlayered style sets the
                            variable too. No new-tab icon in the bar; its screen reader hint stays.
                        -->
                        <SphLink
                            :href="contact.href"
                            :label="contact.label"
                            variant="inherit"
                            class="[--sph-link-underline-rest:0%]! [&_.sph-link\_\_icon]:hidden"
                        />
                    </li>
                </ul>

                <ul
                    v-if="socials.length"
                    class="flex items-center gap-2"
                >
                    <li
                        v-for="social in socials"
                        :key="social.href"
                    >
                        <a
                            :href="social.href"
                            target="_blank"
                            rel="noopener noreferrer"
                            :aria-label="social.label"
                            class="border-primary-foreground/60 hover:bg-primary-foreground/10 focus-visible:ring-primary-foreground/40 flex size-9 items-center justify-center rounded-full border transition-colors outline-none focus-visible:ring-[3px]"
                        >
                            <SphIcon
                                :icon="social.icon"
                                size="sm"
                            />
                        </a>
                    </li>
                </ul>
            </div>
        </div>
    </footer>
</template>

<style>
@reference '@/assets/styles/config.css';

/*
 * The styleguide's floating action button (the pin control, employees only) is fixed to the
 * viewport's bottom right, over the end of this bar; the bar leaves it room on pages that have one.
 * Only from `lg` on: below it the bar does without the extra room.
 * Unscoped: a scoped `:global(body:has(…))` makes the whole rule global and drops the bar from it.
 */
@media (width >= theme(--breakpoint-lg)) {
    body:has(.kgr-actionButton--floating) .sph-page-footer__bar {
        padding-right: calc(2rem + var(--kgr-actionButton-size, 80px) + 1rem);
    }
}
</style>
